// HEIC/HEIF is de iPhone-standaard; browsers kunnen het niet tonen en GCS
// weigert de mimetype (alleen jpeg/png/webp). Zet het in de browser om naar
// JPEG voordat we een upload-URL vragen, zodat iPhone-foto's gewoon meekomen.
// heic2any (libheif-wasm, ~1 MB) wordt pas geladen als er echt een HEIC-bestand
// tussen zit — dynamische import houdt het uit de hoofdbundle.
function isHeic(file: File): boolean {
  return /image\/hei[cf]/i.test(file.type) || /\.hei[cf]$/i.test(file.name);
}

/**
 * Verklein een foto tot hooguit `maxPx` aan de lange kant, als JPEG. Een
 * telefoonfoto van 4000 px en 1,5 MB wordt bij 2000 px zo'n 300 KB, zonder
 * zichtbaar verschil op een scherm. Het opnieuw coderen gooit ook de EXIF weg,
 * dus de GPS-locatie van de foto gaat niet mee de bucket in.
 *
 * Lukt het tekenen niet (oude browser, kapot bestand), dan gaat het origineel
 * mee: liever een grote foto dan een mislukte upload.
 */
export async function shrink(file: File, maxPx: number, quality = 0.82): Promise<File> {
  try {
    // createImageBitmap draait de foto volgens de EXIF-oriëntatie, anders
    // zouden staande telefoonfoto's liggend in het album komen.
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    const scale = Math.min(1, maxPx / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    // JPEG kent geen transparantie; zonder witte ondergrond wordt die zwart.
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();
    const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/jpeg', quality));
    // Al klein genoeg (bijv. een logo als PNG): het origineel is dan beter.
    if (!blob || (scale === 1 && blob.size >= file.size)) return file;
    return new File([blob], file.name.replace(/\.[a-z0-9]+$/i, '') + '.jpg', {
      type: 'image/jpeg',
    });
  } catch {
    return file;
  }
}

export async function toUploadable(file: File, maxPx: number): Promise<File> {
  if (!isHeic(file)) return shrink(file, maxPx);
  const { default: heic2any } = await import('heic2any');
  const out = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
  const blob = Array.isArray(out) ? out[0] : out;
  const jpeg = new File([blob], file.name.replace(/\.hei[cf]$/i, '.jpg'), { type: 'image/jpeg' });
  return shrink(jpeg, maxPx);
}
